import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider.tsx";
import { useEndpointProvider } from "../../../Providers/EndpointProvider";
import { useAuthProvider } from "../../../Providers/AuthProvider";
import { useWorkspacesProvider } from "../../../Providers/WorkspacesProvider";
import { useModalProvider } from "../../../Providers/ModalProvider";
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal";
import { CircularProgress, Box, Typography, IconButton, Modal } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { io } from "socket.io-client";
import { MobergFontFamily, MobergFontSize } from "../../../Components/MobergFont/MobergFont";
import { FRONTEND_LINKS } from "../../../Constants/FrontendLinks";
import RMMobileStickyHeader from "./RMMobileStickyHeader";
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme } from "../../../Moberg";
import { useSetRecoilState } from "recoil";
import { currentPatientFileInfoAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile";
import { DataSource } from "../../../Pages/Data/Visualize/DataReview/Types/DataSource";
import { AlertsStatusDisplay } from "../components/AlertsStatusDisplay";
import StatusBadge from "../components/StatusBadge";
import MobergSnackbar from "../../../Components/MobergSnackbar/MobergSnackbar";
import { GATEWAY_STATUS, URL_PARAMS, MODAL_TYPE, RM_STEPS } from "../constants";

export default function RMMobileGatewayMonitoring(props) {
    const { setNavPage } = props;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const gatewayId = searchParams.get("gateway_id");
    const authProvider = useAuthProvider();
    const { selectedWorkspace } = useWorkspacesProvider();
    
    const [data, setData] = useState(null);
    const { LINKS } = useBackendLinksProvider();
    const endpointProvider = useEndpointProvider();
    const [monitoringStatus, setMonitoringStatus] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [socket, setSocket] = useState(null);
    const [devices, setDevices] = useState([]);
    const [showActionSheet, setShowActionSheet] = useState(false);
    const [dataObjectId, setDataObjectId] = useState(null);
    const setPatientFileInfo = useSetRecoilState(currentPatientFileInfoAtom);
    const [expandedAlert, setExpandedAlert] = useState(null);
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [error, setError] = useState(null);
    const { createModal } = useModalProvider();

    useEffect(() => {
        setNavPage(false);
        
        // Sync URL params for cross-platform compatibility
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has(URL_PARAMS.MODAL)) {
            urlParams.set(URL_PARAMS.MODAL, MODAL_TYPE.REMOTE_MONITORING);
        }
        if (!urlParams.has(URL_PARAMS.STEP)) {
            urlParams.set(URL_PARAMS.STEP, RM_STEPS.MONITORING);
        }
        window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
    }, []);

    const fetchGatewayInfo = async () => {
        setIsLoading(true);
        try {
            const res = await endpointProvider.get(LINKS.AGGREGATOR.GET_GATEWAY_INFO, {
                gateway_id: parseInt(gatewayId, 10)
            });
            setData(res);
            if (res?.gateway?.status) {
                setMonitoringStatus(res.gateway.status);
            }
            if (res?.gateway?.devices) {
                setDevices(res.gateway.devices);
            }
            // Capture data_object_id for alerts
            if (res?.data_object_id) {
                setDataObjectId(res.data_object_id);
                // Update Recoil atom for AlertsProvider
                setPatientFileInfo(previous => ({
                    ...previous,
                    dataSourceMap: new Map(previous.dataSourceMap).set(
                        DataSource.CURRENT_PATIENT, 
                        res.data_object_id
                    )
                }));
            }
            return res;
        } catch (error) {
            console.error("Error fetching gateway info:", error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const openGatewayWebsocket = (gatewayId) => {
        if (socket) {
            socket.disconnect();
        }

        const newSocket = io(`${LINKS.QUERY}/gateways`, { 
            transports: ["websocket"],
            auth: {
                token: authProvider.token ?? "",
                workspace_name: selectedWorkspace ?? ""
            }
        });
        
        newSocket.emit("track", gatewayId);
        
        newSocket.on("gateway_update", (socketData) => {
            if (socketData?.status) {
                setMonitoringStatus(socketData.status);
                setData(prevData => ({
                    ...prevData,
                    gateway: {
                        ...prevData?.gateway,
                        status: socketData.status
                    }
                }));
                
                if (socketData.status === "STOPPED") {
                    setTimeout(() => {
                        navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}?modal=rm&step=SELECT_GATEWAY`);
                    }, 1000);
                }
            }
        });

        newSocket.on("device_update", (socketData) => {
            setDevices(prevDevices => {
                const updatedDevices = [...prevDevices];
                const deviceIndex = updatedDevices.findIndex(device => device.device_id === socketData.id);
                if(deviceIndex !== -1){
                    socketData.device_id = socketData.id;
                    updatedDevices[deviceIndex] = {
                        ...updatedDevices[deviceIndex],
                        ...socketData
                    };
                }
                return updatedDevices;
            })
        });

        setSocket(newSocket);
    };

    useEffect(() => {
        return () => {
            if (socket) {
                socket.disconnect();
            }
        };
    }, [socket]);

    useEffect(() => {
        const fetchData = async () => {
            const info = await fetchGatewayInfo();
            if (info) {
                setData(info);
                let wsGatewayId = info.gateway?.gateway_id;
                if(wsGatewayId){
                    openGatewayWebsocket(wsGatewayId);
                }
                else{
                    console.error("No gateway ID found");
                }
            }
        };
        fetchData();
    }, [gatewayId]);

    const handleToggleMonitoring = async () => {
        if (isLoading) return;

        const isPausing = monitoringStatus === GATEWAY_STATUS.ACTIVE;
        const actionLabel = isPausing ? "Pause" : "Resume";

        setShowActionSheet(false);
        createModal(<ConfirmationModal
            title={`${actionLabel} Monitoring`}
            description={`Are you sure you want to ${actionLabel.toLowerCase()} monitoring for this device?`}
            confirmButton={{
                text: actionLabel,
                theme: MobergTheme.BLUE,
                onClick: async () => {
                    setIsLoading(true);
                    try {
                        const currentInfo = await fetchGatewayInfo();
                        if (currentInfo.gateway.status === GATEWAY_STATUS.STOPPED) {
                            setError("Cannot toggle monitoring when status is STOPPED");
                            return;
                        }

                        setMonitoringStatus(prev => prev === GATEWAY_STATUS.ACTIVE ? GATEWAY_STATUS.PAUSED : GATEWAY_STATUS.ACTIVE);

                        await endpointProvider.post(LINKS.AGGREGATOR.TOGGLE_MONITORING, {
                            gateway_id: parseInt(gatewayId, 10)
                        });

                        const updatedInfo = await fetchGatewayInfo();
                        if (updatedInfo) {
                            setMonitoringStatus(updatedInfo.gateway.status);
                        }
                    } catch (err) {
                        console.error("Error toggling monitoring:", err);
                        await fetchGatewayInfo();
                    } finally {
                        setIsLoading(false);
                    }
                }
            }}
        />);
    };

    const handleStopMonitoring = async () => {
        if (isLoading) return;

        setShowActionSheet(false);
        createModal(<ConfirmationModal
            title="Stop Monitoring"
            description="Are you sure you want to stop monitoring? This action cannot be undone."
            confirmButton={{
                text: "Stop Monitoring",
                theme: MobergTheme.RED,
                onClick: async () => {
                    setIsLoading(true);
                    try {
                        await endpointProvider.post(LINKS.AGGREGATOR.STOP_MONITORING, {
                            gateway_id: parseInt(gatewayId, 10)
                        });
                    } catch (err) {
                        console.error("Error stopping monitoring:", err);
                        const updatedInfo = await fetchGatewayInfo();
                        if (updatedInfo?.gateway?.status === "STOPPED") {
                            navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}?modal=rm&step=SELECT_GATEWAY`);
                        } else {
                            setError("Failed to stop monitoring. Please try again.");
                        }
                    } finally {
                        setIsLoading(false);
                    }
                }
            }}
        />);
    };

    const handleBack = () => {
        navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}?modal=rm&step=SELECT_GATEWAY`);
    };

    const handleLogout = () => {
        authProvider.logout();
        navigate("/");
    };

    const calculateDuration = (startTime) => {
        if (!startTime) return "Unknown";

        const start = new Date(startTime);
        const now = new Date();
        const diff = now - start;

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const formatMeasurements = (wavePriorityList) => {
        if (!wavePriorityList || !Array.isArray(wavePriorityList) || wavePriorityList.length === 0) {
            return "-";
        }

        const measurementsString = wavePriorityList[0];
        if (!measurementsString || typeof measurementsString !== "string") {
            return "-";
        }

        const measurements = measurementsString.split(";").map(m => m.trim()).filter(m => m);
        
        if (measurements.length === 0) {
            return "-";
        }

        // Show all measurements
        return measurements.join(", ");
    };

    const isMonitoringDisabled = () => {
        const status = data?.gateway?.status;
        return isLoading || status === "STOPPED" || status === "STARTING" || status === "PAUSING" || status === "STOPPING";
    };

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#ffffff",
            overflow: "hidden"
        }}>
            <RMMobileStickyHeader 
                title="MCP Device Information"
                showBackButton={true}
                onBack={handleBack}
                onLogout={handleLogout}
            />

            {isLoading && !data ? (
                <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                    <CircularProgress />
                </Box> 
            ) : (
                <>
                    {/* Scrollable Content */}
                    <div style={{
                        flex: 1,
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                        paddingTop: '60px'
                    }}>
                        {/* Content */}
                        <div style={{
                            padding: '16px'
                        }}>
                        {/* MCP Device Card */}
                        <div style={{ 
                            backgroundColor: "#FFFFFF",
                            borderRadius: "4px",
                            padding: "16px",
                            marginBottom: "12px",
                            border: "1px solid #E5E5E5"
                        }}>
                            <div style={{
                                marginBottom: "16px"
                            }}>
                                <Typography variant="h6" style={{ 
                                    fontWeight: 600, 
                                    color: "#2C2C2C", 
                                    fontFamily: MobergFontFamily.HEADER,
                                    fontSize: "16px"
                                }}>
                                    MCP Device
                                </Typography>
                            </div>
                            
                            <div style={{ display: "grid", gap: "12px" }}>
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "12px"
                                }}>
                                    <div>
                                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                                            Name
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: "14px", color: "#2C2C2C" }}>
                                            {data?.gateway_name || "Unknown"}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                                            Status
                                        </div>
                                        <StatusBadge status={data?.gateway?.status} />
                                    </div>
                                </div>

                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "12px"
                                }}>
                                    <div>
                                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                                            Started
                                        </div>
                                        <div style={{ fontSize: "13px", color: "#2C2C2C" }}>
                                            {data?.start_time
                                                ? new Date(data.start_time).toLocaleString('en-GB', { 
                                                    day: '2-digit', 
                                                    month: 'short', 
                                                    year: 'numeric', 
                                                    hour: 'numeric', 
                                                    minute: '2-digit',
                                                    hour12: true 
                                                }).replace(',', ' -').replace(/am|pm/gi, (match) => match.toUpperCase())
                                                : "Unknown"}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                                            Duration
                                        </div>
                                        <div style={{ fontSize: "13px", color: "#2C2C2C" }}>
                                            {calculateDuration(data?.start_time)}
                                        </div>
                                    </div>
                                </div>

                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "12px",
                                    paddingTop: "12px",
                                    borderTop: "1px solid #F0F0F0"
                                }}>
                                    <div>
                                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                                            Patient ID
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: "14px", color: "#2C2C2C" }}>
                                            {data?.patient?.patient_id || "N/A"}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                                            Patient Name
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: "14px", color: "#2C2C2C" }}>
                                            {data?.patient?.patient_name?.trim() ? data.patient.patient_name : "Unknown"}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Alert Status */}
                            {dataObjectId && <AlertsStatusDisplay 
                                excludeAlertTitles={["Window Too Small", "Unsupported Browser"]} 
                                monitoringStatus={monitoringStatus}
                                onExpandClick={(alert) => {
                                    setExpandedAlert(alert);
                                    setShowAlertModal(true);
                                }}
                            />}
                        </div>

                        {/* Devices List */}
                        <div style={{ 
                            backgroundColor: "#FFFFFF",
                            borderRadius: "4px",
                            padding: "16px",
                            border: "1px solid #E5E5E5"
                        }}>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "12px"
                            }}>
                                <Typography variant="h6" style={{ 
                                    fontWeight: 600, 
                                    color: "#2C2C2C", 
                                    fontFamily: MobergFontFamily.HEADER,
                                    fontSize: "16px"
                                }}>
                                    Third-party Devices
                                </Typography>
                                <Typography style={{ 
                                    fontSize: "13px",
                                    color: "#2C2C2C",
                                    fontWeight: 500,
                                    textDecoration: "underline"
                                }}>
                                    {devices.length} connected
                                </Typography>
                            </div>

                            {devices.map((device, index) => {
                                return (
                                    <div 
                                        key={device.device_id || index}
                                        style={{
                                        padding: "12px",
                                        borderRadius: "4px",
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid #E5E5E5",
                                            marginBottom: index < devices.length - 1 ? "8px" : 0
                                        }}
                                    >
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "8px"
                                        }}>
                                            <div style={{ fontWeight: 600, fontSize: "14px", color: "#2C2C2C" }}>
                                                {device.device_type || "Unknown"}
                                            </div>
                                            <StatusBadge status={device.status} sx={{ fontSize: "11px", padding: "4px 8px" }} />
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#666" }}>
                                            {formatMeasurements(device.wave_priority_list)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        </div>
                    </div>

                    {/* Bottom Action Buttons */}
                    <div style={{
                        padding: "16px",
                        backgroundColor: "white",
                        borderTop: "1px solid #E5E5E5",
                        flexShrink: 0
                    }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                            <MobergButton 
                                disabled={isMonitoringDisabled()} 
                                shape={MobergButtonShape.FULL_WIDTH}
                                fontSize={MobergFontSize.LARGE} 
                                theme={MobergTheme.BLUE}
                                variant={MobergButtonVariant.OUTLINE} 
                                onClick={handleToggleMonitoring}
                                style={{height: "55px", fontFamily: MobergFontFamily.HEADER}}
                            >
                                {monitoringStatus === GATEWAY_STATUS.ACTIVE ? "Pause" : "Resume"}
                            </MobergButton>
                            <MobergButton 
                                disabled={isMonitoringDisabled()} 
                                shape={MobergButtonShape.FULL_WIDTH}
                                fontSize={MobergFontSize.LARGE} 
                                variant={MobergButtonVariant.FILLED} 
                                onClick={handleStopMonitoring}
                                style={{
                                    height: "55px", 
                                    fontFamily: MobergFontFamily.HEADER, 
                                    backgroundColor: "#F44336",
                                    border: "none"
                                }}
                            >
                                Stop
                            </MobergButton>
                        </div>
                    </div>
                </>
            )}

            {/* Alert Expand Modal */}
            <Modal
                open={showAlertModal}
                onClose={() => setShowAlertModal(false)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "16px"
                }}
            >
                <Box sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    padding: "16px",
                    maxWidth: "90%",
                    maxHeight: "80%",
                    overflow: "auto",
                    position: "relative",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
                }}>
                    <IconButton
                        onClick={() => setShowAlertModal(false)}
                        sx={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            color: "#666666"
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {expandedAlert && (
                        <>
                            <Typography sx={{
                                fontSize: "18px",
                                fontWeight: 600,
                                color: "#2C2C2C",
                                marginBottom: "8px",
                                paddingRight: "32px",
                                fontFamily: MobergFontFamily.HEADER
                            }}>
                                {expandedAlert.title}
                            </Typography>
                            
                            {/* Time */}
                            {expandedAlert.timestamp && (
                                <Typography sx={{
                                    fontSize: "12px",
                                    color: "#999999",
                                    marginBottom: "12px",
                                    fontFamily: MobergFontFamily.BODY
                                }}>
                                    {(() => {
                                        const estTime = new Date(expandedAlert.timestamp);
                                        estTime.setHours(estTime.getHours() + 3);
                                        return estTime.toLocaleString('en-GB', { 
                                            day: '2-digit', 
                                            month: 'short', 
                                            year: 'numeric', 
                                            hour: 'numeric', 
                                            minute: '2-digit',
                                            hour12: true 
                                        }).replace(',', ' -').replace(/am|pm/gi, (match) => match.toUpperCase());
                                    })()}
                                </Typography>
                            )}
                            
                            {/* Description */}
                            <Typography sx={{
                                fontSize: "14px",
                                color: "#666666",
                                lineHeight: "1.6",
                                marginBottom: expandedAlert.resolutions ? "16px" : 0,
                                fontFamily: MobergFontFamily.BODY
                            }}>
                                {expandedAlert.description}
                            </Typography>
                            
                            {/* Troubleshooting Steps */}
                            {expandedAlert.resolutions && (
                                <>
                                    <Typography sx={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: "#2C2C2C",
                                        marginBottom: "8px",
                                        fontFamily: MobergFontFamily.HEADER
                                    }}>
                                        Troubleshooting Steps
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: "14px",
                                        color: "#666666",
                                        lineHeight: "1.6",
                                        fontFamily: MobergFontFamily.BODY,
                                        whiteSpace: "pre-line"
                                    }}>
                                        {expandedAlert.resolutions}
                                    </Typography>
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Modal>
            
            {/* Error Notification */}
            <MobergSnackbar
                open={!!error}
                onClose={() => setError(null)}
                message={error}
                severity="error"
            />
        </div>
    );
}

